import {
	ReduxPayloadWindowName,
	ReduxPayloadWindowNames,
	ReduxPayloadEmpty,
	ReduxPayloadAddLaunchData,
	ReduxPayloadSetLocation,
	ReduxPayloadWindowTitle,
	ReduxPayloadToggleState,
	ReduxPayloadToggleStateWithTarget,
	ReduxPayloadGroups,
	ReduxPayloadAddToCollection,
	ReduxPayloadCollection,
	ReduxPayloadWorkspaceLoad,
	ReduxPayloadEmulatedMaximization,
	ReduxPayloadScrollTabCollection,
	ReduxPayloadUpdateClosingStatus,
	DraggingStatus,
	DockingOperation,
	WindowTitleBarStore,
	SafetyTimeout,
} from "../types";
import { PhysicalWindowStateEvent, Rectangle } from "../../../../FEA";
import { createAction } from "@reduxjs/toolkit";

/**
 * Export the empty store from here instead of store.ts, so that we don't drag the entire redux server-side
 * infrastructure into our built library.
 */
export const emptyStore: WindowTitleBarStore = {
	windows: {},
	dragging: null,
	tabCollections: {},
	useDOMBasedMovement: true,
	closingTabs: null,
};

/**
 * Actions beginning with `windowTitleBar` represent actions received from the WindowTitleBar UI component. These
 * will generally affect the windowTitleBar slice of the store but may also interact with the main slice.
 *
 * Actions beginning with `main` represent actions received from other locations. These include the window service,
 * the docking system, FEA, windowStartup.ts. These will generally interact with the main slice of the store but
 * may interact with other slices when required.
 */
export const actions = {
	// actions that originate in the WindowTitleBar (i.e. through user actions)
	clone: createAction<ReduxPayloadWindowName>("windowTitleBar/clone"),
	close: createAction<ReduxPayloadEmpty>("windowTitleBar/close"),
	closeTab: createAction<ReduxPayloadWindowName>("windowTitleBar/closeTab"),
	disableDOMBasedMovement: createAction<void>("windowTitleBar/disableDOMBasedMovement"),
	setMaximize: createAction<ReduxPayloadToggleState>("windowTitleBar/maximize"),
	minimize: createAction<ReduxPayloadEmpty>("windowTitleBar/minimize"),
	promoteTab: createAction<ReduxPayloadWindowName>("windowTitleBar/promoteTab"),
	register: createAction<string>("windowTitleBar/register"),
	removeFromTabCollection: createAction<ReduxPayloadSetLocation>("windowTitleBar/removeFromTabCollection"),
	scroll: createAction<ReduxPayloadScrollTabCollection>("windowTitleBar/scrollTabCollection"),
	setAlwaysOnTop: createAction<ReduxPayloadToggleState>("windowTitleBar/setAlwaysOnTop"),
	setGrouped: createAction<ReduxPayloadToggleState>("windowTitleBar/setGrouped"),
	setUserDefinedTitle: createAction<ReduxPayloadWindowTitle>("windowTitleBar/setUserDefinedTitle"),
	setDraggingStatus: createAction<DraggingStatus>("windowTitlebar/setDraggingStatus"),
	stopDragging: createAction<void>("windowTitlebar/stopDragging"),
	shiftPromotedTab: createAction<number>("windowTitleBar/shiftPromotedTab"),
	// closing tabs
	setClosingTabStatus: createAction<ReduxPayloadUpdateClosingStatus>("windowTitleBar/setClosingTabStatus"),
	removeClosingTabs: createAction<string>("windowTitleBar/removeClosingTabs"),

	//api specific Actions
	createTabCollection: createAction<ReduxPayloadCollection>("api/createTabCollection"),
	addTabsToTabCollection: createAction<ReduxPayloadAddToCollection>("api/addTabsToTabCollection"),
	removeTabsFromTabCollection: createAction<ReduxPayloadWindowNames>("api/removeTabsFromTabCollection"),

	//Finsemble UX specific Actions
	showScrim: createAction<string>("ux/showScrim"),
	hideScrim: createAction<void>("ux/hideScrim"),
	setScrimSafetyTimeout: createAction<SafetyTimeout>("ux/setScrimSafetyTimeout"),

	// actions that don't come from the WindowTitleBar.
	addLaunchData: createAction<ReduxPayloadAddLaunchData>("main/addLaunchData"),
	dockingOperation: createAction<DockingOperation>("main/dockingOperation"),
	loadWorkspace: createAction<ReduxPayloadWorkspaceLoad>("main/loadWorkspace"),
	setEmulatedMaximization: createAction<ReduxPayloadEmulatedMaximization>("main/setEmulatedMaximization"),
	enableDND: createAction<ReduxPayloadToggleStateWithTarget>("main/enableDND"),
	loadUserDefinedTitle: createAction<{ windowName: string; title: string | null }>("main/loadUserDefinedTitle"),
	saveSpawnTabTitleToWorkspace: createAction<{ windowName: string; tabTitle: string }>(
		"main/saveSpawnTabTitleToWorkspace"
	),
	setDocumentTitle: createAction<ReduxPayloadWindowTitle>("main/updatedDocumentTitle"),
	setAPITitle: createAction<ReduxPayloadWindowTitle>("main/updatedAPITitle"),
	updateGroupedWindows: createAction<ReduxPayloadGroups>("main/updateGroupedWindows"),
	windowEvent: createAction<PhysicalWindowStateEvent>("main/windowEvent"),
	updateMaximizedBounds: createAction<{ windowName: string; bounds: Rectangle }>("main/updateMaximizedBounds"),
	workspaceClosing: createAction<string[]>("main/workspaceClosing"),
	setTabCollectionForEvergreen: createAction<{ windowName: string; tabCollectionId?: string }>(
		"main/setTabCollectionForEvergreen"
	),
	updateEvergreenAfterWorkspaceLoading: createAction<{}>("main/updateEvergreenAfterWorkspaceLoading"),
	createDefaultWindowTitleBar: createAction<string>("main/createDefaultWindowTitleBar"),
};
